/**
 * ApproachPanel - a group moving quietly as one.
 *
 * Everyone reads who leads and what part each of the company plays, and
 * whether the approach has been made - never how well: that is the GM's
 * until someone looks.
 *
 * Whoever directs names the leader and the parts (each helper the table's
 * assist bonus, each burden -10 on the leader's Silent Move), tests the approach, reads the
 * degrees it was made with, and has the sharpest of the opposition look -
 * which proposes a step for the ladder like any other quiet move. Any
 * change of who does what makes the approach untested again.
 *
 * @param {object} props
 * @param {object} props.approach - hooks/useShellApproach
 * @param {boolean} props.someoneCanLook - there is a watcher to look
 */
import common from '../../styles/common.module.css';
import story from '../../styles/Story.module.css';
import styles from '../../styles/Stealth.module.css';
import { BURDEN_PENALTY, approachMod, partOf } from '../../engine/stealth/approach';

const NOBODY = '';
const FOLLOWS = '';
const signed = (n) => (n >= 0 ? `+${n}` : String(n));

/** How well it was made, for the director's eyes: the degrees, either way. */
function madeWith(degrees) {
  if (degrees === null) return 'Not tested yet.';
  const n = Math.abs(degrees);
  const unit = `degree${n === 1 ? '' : 's'}`;
  return degrees >= 0 ? `Made with ${n} ${unit} of success.` : `Failed by ${n} ${unit}.`;
}

function LeaderSelect({ approach }) {
  return (
    <label className={story.field}>
      Led by
      <select
        className={story.input}
        data-testid="approach-leader"
        value={approach.approach.leaderId ?? NOBODY}
        onChange={(e) => approach.setLeader(e.target.value || null)}
      >
        <option value={NOBODY}>Nobody leads</option>
        {approach.company.map((c) => (
          <option key={c.id} value={c.id}>
            {c.name}
          </option>
        ))}
      </select>
    </label>
  );
}

/** One of the company other than the leader: their part, set by the director. */
function PartRow({ person, approach }) {
  return (
    <li className={styles.who}>
      <span className={styles.whoName}>{person.name}</span>
      <select
        className={story.input}
        aria-label={`${person.name}'s part in the approach`}
        data-testid={`approach-role-${person.id}`}
        value={approach.approach.roles[person.id] ?? FOLLOWS}
        onChange={(e) => approach.setRole(person.id, e.target.value || null)}
      >
        <option value={FOLLOWS}>Follows</option>
        <option value="helper">Assists ({signed(approach.helperBonus)})</option>
        <option value="burden">A burden ({signed(-BURDEN_PENALTY)})</option>
      </select>
    </li>
  );
}

function DirectorView({ approach, someoneCanLook }) {
  const { leaderId } = approach.approach;
  const others = approach.company.filter((c) => c.id !== leaderId);
  return (
    <>
      <LeaderSelect approach={approach} />
      {leaderId && others.length > 0 && (
        <ul className={story.list}>
          {others.map((person) => (
            <PartRow key={person.id} person={person} approach={approach} />
          ))}
        </ul>
      )}
      <p className={story.empty} data-testid="approach-degrees">
        {leaderId
          ? `Silent Move at ${signed(approachMod(approach.approach, approach.helperBonus))} for the company. `
          : ''}
        {madeWith(approach.degrees)}
      </p>
      <div className={story.tools}>
        <button
          type="button"
          className={common.btnSmall}
          data-testid="approach-test"
          disabled={!leaderId}
          onClick={approach.test}
        >
          {approach.approach.tested ? 'Test the approach again' : 'Test the approach'}
        </button>
        <button
          type="button"
          className={common.btnSmall}
          data-testid="approach-look"
          disabled={approach.degrees === null || !someoneCanLook}
          onClick={approach.look}
        >
          Someone looks
        </button>
      </div>
    </>
  );
}

function PlayerView({ approach }) {
  const { leaderId, tested } = approach.approach;
  const leader = approach.company.find((c) => c.id === leaderId);
  if (!leader) return <p className={story.empty}>Nobody is leading an approach.</p>;
  return (
    <>
      <ul className={story.list}>
        {approach.company.map((c) => (
          <li key={c.id} className={styles.who} data-testid="approach-part">
            <span className={styles.whoName}>{c.name}</span>
            <span className={styles.state}>{partOf(approach.approach, c.id)}</span>
          </li>
        ))}
      </ul>
      <p className={story.empty} data-testid="approach-hidden">
        {tested ? 'How well it went is not shown until someone looks.' : 'Not tested yet.'}
      </p>
    </>
  );
}

export default function ApproachPanel({ approach, someoneCanLook }) {
  return (
    <section className={story.section} aria-label="The approach" data-testid="approach-panel">
      <h3 className={story.caption}>The approach</h3>
      {approach.canDirect ? (
        <DirectorView approach={approach} someoneCanLook={someoneCanLook} />
      ) : (
        <PlayerView approach={approach} />
      )}
    </section>
  );
}
